import React from 'react';
import { Box, Container } from '@mui/material';

interface PageLayoutProps {
    children: React.ReactNode;
    /** Max content width. Defaults to `xl` (1536px) so tables don't get cramped on wide screens. */
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
    disableContainer?: boolean;
}

const DRAWER_WIDTH = 260;

/**
 * Responsive shell:
 *   - xs / sm / md: drawer is a temporary overlay, content takes full width.
 *   - lg+:          drawer is permanent, content sits to its right.
 * The breakpoint here MUST match `Navbar.tsx` so width math stays in sync.
 */
const PageLayout: React.FC<PageLayoutProps> = ({
    children,
    maxWidth = 'xl',
    disableContainer = false,
}) => {
    return (
        <Box
            component="main"
            sx={{
                flexGrow: 1,
                py: { xs: 2, sm: 3 },
                px: { xs: 1, sm: 2 },
                mt: { xs: 7, sm: 8 }, // AppBar height (56 mobile / 64 desktop)
                width: {
                    xs: '100%',
                    lg: `calc(100% - ${DRAWER_WIDTH}px)`,
                },
                ml: {
                    xs: 0,
                    lg: `${DRAWER_WIDTH}px`,
                },
                minHeight: 'calc(100vh - 64px)',
                // Make sure long table rows can scroll horizontally instead of
                // clipping or stretching the whole page.
                overflowX: 'hidden',
            }}
        >
            {disableContainer ? (
                children
            ) : (
                <Container
                    maxWidth={maxWidth}
                    sx={{
                        // Tighter gutters on phones so the content gets more room.
                        px: { xs: 1.5, sm: 2, md: 3 },
                    }}
                >
                    {children}
                </Container>
            )}
        </Box>
    );
};

export default PageLayout;